import { useEffect, useState, type MouseEvent } from "react";
import { getAvatarUrl } from "../../api/whatsapp.js";
import { useAppDispatch } from "../../app/AppProvider.tsx";
import { fmtTimestamp } from "../../lib/dom.js";
import type { ChatTag, WhatsappChat, WhatsappMessage } from "../../types.ts";
import {
  avatarLookupJidForChat,
  avatarPatchForChat,
  displayNameForChat,
  effectiveMessageKind,
  messageStatusForChatPreview,
  persistedOutgoingAckStatus,
  previewKindPrefix,
  previewTextForChat,
} from "../Chat.helpers.ts";
import { ChatAvatar, MessageAck, SvgIcon } from "./ChatAvatar.tsx";
import { icons } from "./icons.ts";

export function ChatListItem({
  chat,
  messages,
  tags,
  active,
  onOpen,
  onContextMenu,
}: {
  chat: WhatsappChat;
  messages: WhatsappMessage[];
  tags: ChatTag[];
  active: boolean;
  onOpen: () => void;
  onContextMenu: (event: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
}) {
  const dispatch = useAppDispatch();
  const display = displayNameForChat(chat);
  const previewMessage = lastLoadedPreviewMessage(chat, messages);
  const previewKind = previewMessage ? effectiveMessageKind(previewMessage) : chat.lastKind;
  const previewText = previewTextForChat({ lastText: chat.lastText, lastKind: previewKind });
  const previewStatus = messageStatusForChatPreview(chat, messages);
  const ackStatus = chat.lastFromMe ? persistedOutgoingAckStatus(previewStatus, chat.provider) : previewStatus;
  const unread = (chat.unreadCount || 0) > 0;
  const lookupJid = avatarLookupJidForChat(chat);
  const providedAvatarUrl = typeof chat.avatarUrl === "string" ? chat.avatarUrl : null;
  const [avatarUrl, setAvatarUrl] = useState<string | null>(providedAvatarUrl);

  useEffect(() => {
    if (providedAvatarUrl) {
      setAvatarUrl(providedAvatarUrl);
      return;
    }
    if (!lookupJid) {
      setAvatarUrl(null);
      return;
    }
    let activeEffect = true;
    getAvatarUrl(lookupJid, { cacheOnly: true, syncAvatar: true }).then((url: string | null) => {
      if (url) return url;
      return getAvatarUrl(lookupJid, { syncAvatar: true });
    }).then((url: string | null) => {
      if (!activeEffect || !url) return;
      setAvatarUrl(url);
      dispatch({ type: "PATCH_CHAT", chat: avatarPatchForChat(chat, url) });
    }).catch(() => undefined);
    return () => {
      activeEffect = false;
    };
  }, [chat, dispatch, lookupJid, providedAvatarUrl]);

  return (
    <div
      className={`wa-chat-item ${active ? "is-active" : ""}`}
      onClick={onOpen}
      onContextMenu={onContextMenu}
    >
      <ChatAvatar chat={chat} display={display} avatarUrl={avatarUrl} />
      <div className="wa-chat-meta">
        <div className="wa-chat-row1">
          <span className="wa-chat-name">{display}</span>
          <span className="wa-chat-tags-dots">
            <TagDots chat={chat} tags={tags} />
          </span>
          <span className="wa-chat-time">{fmtTimestamp(chat.lastTsMs)}</span>
        </div>
        <div className="wa-chat-row2">
          <span className="wa-chat-preview">
            <MessageAck fromMe={chat.lastFromMe} status={ackStatus} />
            {previewKindPrefix(previewKind) ? <span className="wa-chat-kind">{previewKindPrefix(previewKind)}</span> : null}
            <span className="wa-chat-preview-text">{previewText}</span>
          </span>
          <span className="wa-chat-tail">
            {chat.pinnedAt ? <span className="wa-chat-pin" title="Fixada"><SvgIcon svg={icons.pin} /></span> : null}
            {unread ? <span className="wa-chat-unread">{chat.unreadCount}</span> : null}
            <button
              className="wa-chat-menubtn"
              type="button"
              title="Mais opcoes"
              onClick={(event) => {
                event.stopPropagation();
                onContextMenu(event);
              }}
            >
              <SvgIcon svg={icons.chevron} />
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

function lastLoadedPreviewMessage(chat: WhatsappChat, messages: WhatsappMessage[]) {
  if (!messages.length) return null;
  const lastMsgId = String(chat.lastMsgId || "");
  if (lastMsgId) {
    return messages.find((message) => message.messageKey === lastMsgId) || null;
  }
  return messages[messages.length - 1] || null;
}

function TagDots({ chat, tags }: { chat: WhatsappChat; tags: ChatTag[] }) {
  const tagMap = new Map(tags.map((tag) => [tag.id, tag]));
  return (
    <>
      {(chat.tagIds || []).slice(0, 3).map((id) => {
        const tag = tagMap.get(id);
        return tag ? <span key={id} className="wa-tag-dot" title={tag.name} style={{ background: tag.color }} /> : null;
      })}
    </>
  );
}
